import type { ApiClient } from '../client';
import { AUTH_PATHS, TENANT_PATHS } from '../../../config/api-paths';
import type { ApiResponse, ResolveTenantResponse } from '../types';
import type {
  ChangePasswordRequest,
  ChangePasswordResponse,
  PasswordResetConfirmRequest,
  PasswordResetConfirmResponse,
  PasswordResetRequest,
  PasswordResetRequestResponse,
  PublicAuthConfig,
  ZohoOAuthAuthorizeResponse,
  ZohoOAuthCallbackRequest,
  ZohoOAuthLoginResponse,
  GoogleOAuthAuthorizeResponse,
  GoogleOAuthCallbackRequest,
  GoogleOAuthLoginResponse,
} from '../types/auth';

export function bindAuth(client: ApiClient) {
  return {
    async getPublicConfig(): Promise<ApiResponse<PublicAuthConfig & Record<string, unknown>>> {
      return client.request<PublicAuthConfig & Record<string, unknown>>(AUTH_PATHS.config, {
        skipAuth: true,
      });
    },

    async resolveTenant(params: {
      domain?: string;
      slug?: string;
    }): Promise<ApiResponse<ResolveTenantResponse>> {
      const qs = new URLSearchParams();
      if (params.domain?.trim()) qs.set('domain', params.domain.trim());
      if (params.slug?.trim()) qs.set('slug', params.slug.trim());
      const query = qs.toString();
      const path = query ? `${TENANT_PATHS.resolve}?${query}` : TENANT_PATHS.resolve;
      return client.request<ResolveTenantResponse>(path, { skipAuth: true });
    },

    async changePassword(body: ChangePasswordRequest): Promise<ApiResponse<ChangePasswordResponse>> {
      return client.request<ChangePasswordResponse>(AUTH_PATHS.passwordChange, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async logout(): Promise<ApiResponse<{ message: string }>> {
      return client.request<{ message: string }>(AUTH_PATHS.logout, {
        method: 'POST',
        suppressSessionExpiry: true,
      });
    },

    async rotateSession(): Promise<ApiResponse<Record<string, unknown>>> {
      return client.request<Record<string, unknown>>(AUTH_PATHS.sessionRotate, {
        method: 'POST',
      });
    },

    async requestPasswordReset(
      body: PasswordResetRequest
    ): Promise<ApiResponse<PasswordResetRequestResponse>> {
      return client.request<PasswordResetRequestResponse>(AUTH_PATHS.passwordResetRequest, {
        method: 'POST',
        body: JSON.stringify(body),
        skipAuth: true,
      });
    },

    async confirmPasswordReset(
      body: PasswordResetConfirmRequest
    ): Promise<ApiResponse<PasswordResetConfirmResponse>> {
      return client.request<PasswordResetConfirmResponse>(AUTH_PATHS.passwordResetConfirm, {
        method: 'POST',
        body: JSON.stringify(body),
        skipAuth: true,
      });
    },

    async getZohoOAuthAuthorizeUrl(): Promise<ApiResponse<ZohoOAuthAuthorizeResponse>> {
      return client.request<ZohoOAuthAuthorizeResponse>(AUTH_PATHS.zohoAuthorize, { skipAuth: true });
    },

    async completeZohoOAuthLogin(
      body: ZohoOAuthCallbackRequest
    ): Promise<ApiResponse<ZohoOAuthLoginResponse>> {
      return client.request<ZohoOAuthLoginResponse>(AUTH_PATHS.zohoCallback, {
        method: 'POST',
        body: JSON.stringify(body),
        skipAuth: true,
      });
    },

    async getGoogleOAuthAuthorizeUrl(): Promise<ApiResponse<GoogleOAuthAuthorizeResponse>> {
      return client.request<GoogleOAuthAuthorizeResponse>(AUTH_PATHS.googleAuthorize, { skipAuth: true });
    },

    async completeGoogleOAuthLogin(
      body: GoogleOAuthCallbackRequest
    ): Promise<ApiResponse<GoogleOAuthLoginResponse>> {
      return client.request<GoogleOAuthLoginResponse>(AUTH_PATHS.googleCallback, {
        method: 'POST',
        body: JSON.stringify(body),
        skipAuth: true,
      });
    },
  };
}
